import { useRef } from "react";
import { useDebouncedSave, useServerDraft } from "../../lib/autosave";
import type { IssueDetail } from "../../types";
import { SectionHeader } from "../page";
import { Markdown } from "./markdown";
import { MarkdownEditor, normalizeMarkdown } from "./markdown-editor";

export function TestingInstructionsBlock({
  issue,
  pending,
  saved,
  onSave,
  readOnly = false,
}: {
  issue: IssueDetail;
  pending: boolean;
  saved: boolean;
  onSave: (patch: { testingInstructions?: string }) => void;
  readOnly?: boolean;
}) {
  if (readOnly) {
    const instructions = issue.testingInstructions?.trim();
    if (!instructions) return null;
    return (
      <section aria-label="Testing instructions">
        <SectionHeader title="Testing instructions" className="mb-1" />
        <Markdown>{instructions}</Markdown>
      </section>
    );
  }
  return <EditableInstructions issue={issue} pending={pending} saved={saved} onSave={onSave} />;
}

function EditableInstructions({
  issue,
  pending,
  saved,
  onSave,
}: {
  issue: IssueDetail;
  pending: boolean;
  saved: boolean;
  onSave: (patch: { testingInstructions?: string }) => void;
}) {
  const savedInstructions = issue.testingInstructions ?? "";
  const [value, setValue, markSent] = useServerDraft(savedInstructions, normalizeMarkdown);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const normalized = normalizeMarkdown(value);
  const savedValue = normalizeMarkdown(savedInstructions);

  useDebouncedSave(normalized, savedValue, (next) => {
    markSent(next);
    onSaveRef.current({ testingInstructions: next });
  });

  const dirty = normalized !== savedValue;
  const hint = pending || dirty ? "Saving…" : saved ? "Saved" : undefined;

  return (
    <MarkdownEditor
      title="Testing instructions"
      label="Testing instructions"
      placeholder="How should a reviewer check this? Type / for commands…"
      initial={savedInstructions}
      value={value}
      onChange={setValue}
      hint={hint}
    />
  );
}
